import { z } from 'zod';

export const connectedAppProviderSchema = z.enum(['secrets-manager', 'managed-secret-ref', 'obo-target']);
export type ConnectedAppProvider = z.infer<typeof connectedAppProviderSchema>;

/**
 * An OAuth client app (client ID, issuer and secret), or an on-behalf-of target: the downstream API a user's token
 * is exchanged for, which holds no credentials because the exchange is made as the sign-in app.
 */
export const connectedAppKindSchema = z.enum(['client', 'obo-target']);
export type ConnectedAppKind = z.infer<typeof connectedAppKindSchema>;

export function getConnectedAppKind(provider: string): ConnectedAppKind {
  return provider === 'obo-target' ? 'obo-target' : 'client';
}

export const connectedAppOboTargetDataSchema = z.object({
  /** The scope requested for the downstream API, e.g. `<resource app id>/.default`. */
  scopes: z.string().min(1, 'Scope is required'),
  /** Sent as the exchange's `resource` parameter when set. Most Entra targets need only the scope. */
  audience: z.string().prefault(''),
});
export type ConnectedAppOboTargetData = z.infer<typeof connectedAppOboTargetDataSchema>;

export const connectedAppConfigSchema = z.object({
  audience: z.string().prefault(''),
  clientId: z.string(),
  scopes: z.string(),
  issuerUrl: z.string(),
});

export const connectedAppLegacySecretDataSchema = connectedAppConfigSchema.extend({
  clientSecret: z.string(),
});

// Runtime OAuth data used server-side after all secret references are resolved.
export const connectedAppDataSchema = connectedAppLegacySecretDataSchema;

export const connectedAppManagedSecretRefDataSchema = connectedAppConfigSchema.extend({
  clientSecretManagedSecretIdentifier: z.string(),
});

// API responses never return the raw client secret.
export const connectedAppDetailsDataSchema = connectedAppConfigSchema.extend({
  clientSecretManagedSecretIdentifier: z.string().nullable(),
});

export const connectedAppsTableItemSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  kind: connectedAppKindSchema,
  createdOn: z.coerce.date(),
  createdBy: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable(),
});

export const connectedAppSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  secretRef: z.string().nullable(),
  provider: connectedAppProviderSchema,
  name: z.string(),
});
export type ConnectedApp = z.infer<typeof connectedAppSchema>;

const connectedAppDetailsBaseSchema = z.object({
  id: z.coerce.number(),
  identifier: z.string(),
  name: z.string(),
});

export const connectedAppDetailsSchema = z.discriminatedUnion('provider', [
  connectedAppDetailsBaseSchema.extend({
    provider: z.literal('secrets-manager'),
    data: connectedAppDetailsDataSchema,
  }),
  connectedAppDetailsBaseSchema.extend({
    provider: z.literal('managed-secret-ref'),
    data: connectedAppDetailsDataSchema,
  }),
  connectedAppDetailsBaseSchema.extend({
    provider: z.literal('obo-target'),
    data: connectedAppOboTargetDataSchema,
  }),
]);

export type ConnectedAppDetails = z.infer<typeof connectedAppDetailsSchema>;
